// «С ИИ» в редакторе шаблона и на тренировке: просьба словами («добавь 2 упражнения на бицепс»)
// → ответ ИИ и список правок → «Применить». Сервер ничего не сохраняет; разговор живёт, пока
// открыт экран, и уходит ИИ вместе с просьбой — можно уточнять («нет, замени второе»).
// Упражнение не из справочника заменяется похожим или убирается прямо в чате.

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { Button, ErrorText, Sheet, Spinner, controlClass } from '@/components/ui'
import { cn } from '@/lib/cn'

import { useAiStatus, useEditWithAi, type EditItem, type EditMode, type EditOperation, type EditReply } from './api'
import { describeOp, dropOperation, editHistory, pickSuggestion, unresolved, type EditMessage } from './edit'
import { matchNote } from './logic'
import { Disclaimer, Markdown } from './parts'

const EXAMPLES: Record<EditMode, string[]> = {
  template: ['Добавь 2 упражнения на бицепс', 'Сделай везде по 4 подхода', 'Замени первое упражнение на гантели'],
  workout: ['Добавь упражнение на пресс в конец', 'Добавь 2 упражнения на бицепс', 'Убавь подходы у оставшихся'],
}

function OperationRow({
  op,
  nameOf,
  editable,
  onPick,
  onDrop,
}: {
  op: EditOperation
  nameOf: (key: string | null) => string
  editable: boolean
  onPick: (s: { id: string; name: string; tracking: string }) => void
  onDrop: () => void
}) {
  const { sign, text } = describeOp(op, nameOf)
  const unknown = op.exercise !== null && op.exercise.exercise_id === null
  const note = op.exercise ? matchNote(op.exercise) : null
  return (
    <li className={cn('grid gap-1 rounded-lg border p-2.5', unknown ? 'border-warning/40 bg-warning-soft/40' : 'border-border')}>
      <div className="flex items-start gap-2 text-sm">
        <span aria-hidden="true" className="w-4 shrink-0 text-center font-semibold text-accent">
          {sign}
        </span>
        <span className="min-w-0 flex-1 text-fg">{text}</span>
      </div>
      {note ? <p className="pl-6 text-xs text-muted">{note}</p> : null}
      {op.exercise?.notes ? <p className="pl-6 text-xs text-muted">{op.exercise.notes}</p> : null}
      {unknown && editable ? (
        <div className="grid gap-2 pl-6">
          <p className="text-sm text-fg">«{op.exercise!.name}» нет в справочнике — замени похожим или убери.</p>
          <div className="flex flex-wrap gap-2">
            {op.exercise!.suggestions.map((s) => (
              <Button key={s.id} size="sm" onClick={() => onPick(s)}>
                {`Заменить на «${s.name}»`}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={onDrop}>
              Убрать
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

function Answer({
  reply,
  applied,
  last,
  nameOf,
  applying,
  onChange,
  onApply,
}: {
  reply: EditReply
  applied: boolean
  last: boolean
  nameOf: (key: string | null) => string
  applying: boolean
  onChange: (reply: EditReply) => void
  onApply: () => void
}) {
  const editable = last && !applied
  return (
    <div className="grid gap-2 rounded-xl bg-surface-2/60 p-3">
      <Markdown text={reply.reply} />
      {reply.operations.length > 0 ? (
        <ul className="grid gap-1.5" aria-label="Правки">
          {reply.operations.map((op, i) => (
            <OperationRow
              key={i}
              op={op}
              nameOf={nameOf}
              editable={editable}
              onPick={(s) => onChange(pickSuggestion(reply, i, s))}
              onDrop={() => onChange(dropOperation(reply, i))}
            />
          ))}
        </ul>
      ) : null}
      {reply.skipped.length > 0 ? (
        <ul className="grid gap-0.5 text-xs text-warning">
          {reply.skipped.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      ) : null}
      {applied ? (
        <p className="text-sm font-medium text-accent">Применено</p>
      ) : editable && reply.operations.length > 0 ? (
        <Button
          variant="primary"
          icon="check"
          className="justify-self-start"
          loading={applying}
          disabled={unresolved(reply.operations)}
          onClick={onApply}
        >
          Применить
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Кнопка-панель «С ИИ». `items` — текущие упражнения (с ключами клиента), `onApply` — перенести
 * правки; ключи упражнений, которых уже нет, пропускаются там же.
 */
export function AiEditSheet({
  open,
  onClose,
  mode,
  title,
  items,
  onApply,
}: {
  open: boolean
  onClose: () => void
  mode: EditMode
  title: string | null
  items: EditItem[]
  onApply: (operations: EditOperation[]) => Promise<void> | void
}) {
  const status = useAiStatus()
  const edit = useEditWithAi()
  const [messages, setMessages] = useState<EditMessage[]>([])
  const [text, setText] = useState('')
  const [applying, setApplying] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)
  const configured = status.data?.configured === true

  const current = new Map(items.map((item) => [item.key, item.name]))
  const nameOf =
    (names: Record<string, string>) =>
    (key: string | null): string =>
      (key ? (current.get(key) ?? names[key]) : undefined) ?? 'упражнение'

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' })
  }, [messages.length, edit.isPending])

  const send = (instruction: string) => {
    const value = instruction.trim()
    if (!value || edit.isPending) return
    const history = editHistory(messages)
    const names = Object.fromEntries(items.map((item) => [item.key, item.name]))
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', text: value }])
    setText('')
    edit.mutate(
      { mode, title, instruction: value, exercises: items, history },
      {
        onSuccess: (reply) =>
          setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', reply, applied: false, names }]),
      },
    )
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    send(text)
  }

  const apply = async (id: string, reply: EditReply) => {
    setApplying(true)
    try {
      await onApply(reply.operations)
      setMessages((prev) => prev.map((m) => (m.id === id && m.role === 'assistant' ? { ...m, applied: true } : m)))
    } finally {
      setApplying(false)
    }
  }

  const lastId = messages.at(-1)?.id
  const footer = configured ? (
    <form onSubmit={submit} className="flex w-full items-end gap-2">
      <label htmlFor="ai-edit-text" className="sr-only">
        Что поменять
      </label>
      <textarea
        id="ai-edit-text"
        rows={2}
        maxLength={1000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            send(text)
          }
        }}
        placeholder={mode === 'workout' ? 'Например: добавь упражнение на пресс' : 'Например: добавь 2 упражнения на бицепс'}
        className={cn(controlClass, 'h-auto min-h-11 flex-1 resize-none py-2')}
      />
      <Button type="submit" variant="primary" icon="ai" loading={edit.isPending} disabled={!text.trim()}>
        Отправить
      </Button>
    </form>
  ) : undefined

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={mode === 'workout' ? 'Тренировка с ИИ' : 'Шаблон с ИИ'}
      description={
        mode === 'workout'
          ? 'Скажи, что добавить или поменять. Упражнения с выполненными подходами не меняются.'
          : 'Скажи, что добавить, убрать или поменять. Сохранится по кнопке «Сохранить».'
      }
      fullMobile
      wide
      footer={footer}
    >
      {status.isSuccess && !configured ? (
        <p className="text-sm text-muted">
          ИИ не настроен —{' '}
          <Link to="/settings/ai" className="text-accent hover:underline">
            настроить
          </Link>
          .
        </p>
      ) : (
        <div className="grid gap-3">
          {messages.length === 0 ? (
            <div className="grid gap-2">
              <p className="text-sm text-muted">Например:</p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLES[mode].map((example) => (
                  <Button key={example} size="sm" onClick={() => send(example)} disabled={edit.isPending}>
                    {example}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
          {messages.map((m) =>
            m.role === 'user' ? (
              <p key={m.id} className="max-w-[85%] justify-self-end whitespace-pre-wrap rounded-xl bg-accent-soft px-3 py-2 text-sm text-fg">
                {m.text}
              </p>
            ) : (
              <Answer
                key={m.id}
                reply={m.reply}
                applied={m.applied}
                last={m.id === lastId}
                nameOf={nameOf(m.names)}
                applying={applying}
                onChange={(reply) => setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...m, reply } : x)))}
                onApply={() => void apply(m.id, m.reply)}
              />
            ),
          )}
          {edit.isPending ? (
            <p role="status" className="flex items-center gap-2 text-sm text-muted">
              <Spinner size={16} /> ИИ думает — это может занять до минуты.
            </p>
          ) : null}
          {edit.isError ? <ErrorText error={edit.error} /> : null}
          {messages.length > 0 ? <Disclaimer /> : null}
          <div ref={endRef} />
        </div>
      )}
    </Sheet>
  )
}
